<template>
  <div class="employee-form">
    <div class="info-title">
      <div class="title">{{$t('employees.page_title')}}</div>
      <div class="subtitle">{{$t('employees.page_subtitle')}}</div>
    </div>

    <div class="subtitle-2">{{$t('employees.personal_info')}}</div>
    <div class="columns">
      <div class="column">
        <div class="group">
          <label>{{$t('user.first_name')}}</label>
          <input type="text" v-model.trim="value.firstName" :class="{invalid: validate && validate.error('employee.firstName')}">
        </div>
        <div class="group">
          <label>{{$t('user.last_name')}}</label>
          <input type="text" v-model.trim="value.lastName" :class="{invalid: validate && validate.error('employee.lastName')}">
        </div>
        <div class="group" v-if="value.preferences">
          <div class="group__name-tooltip">
            <label>{{$t('employees.preferences.publicName')}}</label>
            <Tooltip :text="$t('employees.preferences.publicName_tooltip')"/>
          </div>
          <input v-model.trim="value.preferences.publicName" type="text">
        </div>
      </div>

      <div class="column">
        <div class="group" v-if="value.id">
          <br>
          <div>
            <button class="btn btn-primary" @click="changePassword = !changePassword">{{$t('employees.change_password')}}</button>
          </div>
          <br v-if="changePassword">
          <input type="text" v-model="value.password" placeholder="No password" v-if="changePassword">
        </div>
        <div class="group" v-if="!value.id">
          <label>{{$t('employees.password')}}</label>
          <input type="text" v-model="value.password" placeholder="No password">
        </div>
      </div>
    </div>

    <!-- Preferences/Config -->
    <template v-if="value.preferences">
      <div class="separator"></div>
      <div class="subtitle-2">{{$t('employees.preferences.title')}}</div>
      <div class="columns preferences">
        <div class="column">
          <div class="group">
            <label>{{$t('employees.preferences.hand_preference')}}</label>
            <div class="radio_group">
              <div class="radio">
                <input type="radio" id="chk_leftHanded_true" :value="true" v-model="value.preferences.leftHanded">
                <label for="chk_leftHanded_true">{{$t('employees.preferences.left_handed')}}</label>
              </div>
              <div class="radio">
                <input type="radio" id="chk_leftHanded_false" :value="false" v-model="value.preferences.leftHanded">
                <label for="chk_leftHanded_false">{{$t('employees.preferences.right_handed')}}</label>
              </div>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="group">
            <label>{{$t('employees.preferences.language')}}</label>
            <div class="radio_group">
              <div class="radio" v-for="language of languages" :key="'lang' + language">
                <input type="radio" :id="'chk_lang' + language" :value="language" v-model="value.preferences.language">
                <label :for="'chk_lang' + language">{{$t(`translation.${language}`)}}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- assignation to company and role -->
    <div class="separator"></div>
    <div class="subtitle-2">{{$t('employees.assignation')}}</div>


    <div class="company-roles" v-if="ready">
      <div class="company-role" v-for="(companyAssociation, associationIndex) of value.companies" :key="'cr-' + associationIndex">
        <div class="company-role__icon">
          <svgicon src="icon/employee.svg" :height="16" :width="16"></svgicon>
        </div>
        <div class="company-role__center">
          <div class="company-role__section">
            <label>{{$t('branch.company')}}</label>
            <select v-model="companyAssociation.id">
              <option :value="null" disabled hidden>{{$t('choose')}}...</option>
              <option :value="company.id" v-for="company of companies"
                      :disabled="company.id !== companyAssociation.id && selectedCompanies.includes(company.id)"
                      :key="`c_${associationIndex}_${company.id}`">{{company.name}}</option>
            </select>
          </div>
          <div class="line"></div>
          <div class="company-role__section">
            <label>PIN</label>
            <input type="text" class="pin-input" v-model.trim="companyAssociation.pin" :size="Math.max(5, companyAssociation.pin.length)-1">
          </div>
          <div class="line"></div>
          <div class="company-role__section" :class="{locked: !companyAssociation.id}">
            <label>{{$t('pos_roles.role')}}</label>
            <select v-model="companyAssociation.posRole.id">
              <option :value="null">{{$t('choose')}}...</option>
              <option :value="role.id" v-for="role of getCompanyRoles(companyAssociation.id)"
                      :key="`r_${associationIndex}_${role.id}`">{{role.name}}</option>
            </select>
          </div>
          <div class="line"></div>
          <div class="company-role__section" :class="{locked: !companyAssociation.id || getCompanyRevenueCenters(companyAssociation.id).length === 0}">
            <label>{{$t('revenue_centers.entity')}}</label>
            <select v-model="companyAssociation.revenueCenter.id">
              <option :value="null" v-if="getCompanyRevenueCenters(companyAssociation.id).length > 0">{{$t('choose')}}...</option>
              <option :value="null" v-if="getCompanyRevenueCenters(companyAssociation.id).length === 0">{{$t('not_available')}}</option>
              <option :value="center.id" v-for="center of getCompanyRevenueCenters(companyAssociation.id)"
                      :key="`rc_${associationIndex}_${center.id}`">{{$to(center.name)}}</option>
            </select>
          </div>
        </div>
        <div class="company-role__action-icon" @click="removeCompanyAssociation(associationIndex)">
          <svgicon src="icon/delete.svg" :height="16" :width="16"></svgicon>
        </div>
      </div>
    </div>

    <div class="company-role__new" @click="addCompanyAssociation">
      <div class="new__inner">
        <svgicon src="icon/circle-add.svg" :height="12" :width="12"></svgicon>
        {{$t('employees.add_company')}}
      </div>
    </div>

  </div>
</template>

<script>
import Vue from "vue";
import {mapActions} from "vuex";
import Tooltip from '@/components/tools/Tooltip.vue';

export default {
  name: "EmployeeForm",

  components: { Tooltip },

  props: {
    value: Object,
    validation: {
      type: Object,
      default: null
    },
    branchId: {
      type: String|Number,
      required: true
    }
  },

  data() {
    return {
      ready: false,
      changePassword: false,
      companies: []
    }
  },

  computed: {
    languages() {
      return ['fr', 'en', 'es'];
    },
    validate() {
      return this.validation;
    },
    selectedCompanies() {
      return this.value.companies.map(c => c.id);
    }
  },

  async mounted() {
    showSpinner();
    await this.loadCompanies();
    this.ready = true;
    hideSpinner();
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        for (let c of val.companies) {
          c.posRole = c.posRole || {id: null}
          c.revenueCenter = c.revenueCenter || {id: null}
        }
      }
    },

    changePassword: {
      immediate: true,
      handler(val) {
        if (val) {
          Vue.set(this.value, 'password', '');
        } else {
          Vue.delete(this.value, 'password');
        }
      }
    },

    "value.preferences.publicName": {
      immediate: true,
      handler(val) {
        if (val === '') {
          Vue.delete(this.value.preferences, 'publicName');
        }
      }
    }
  },

  methods: {
    ...mapActions(["getCompanies", "getPosRoles", "getRevenueCenters"]),

    async loadCompanies() {
      /* TODO put posRoles and revenueCenters in company api call somehow */
      let response = await this.getCompanies(this.branchId);
      this.companies = [];
      for (let c of response) {
        let [rolesResponse, centersResponse] = await Promise.all([
            this.getPosRoles(c.id),
            this.getRevenueCenters(c.id)
        ]);
        this.companies.push({
          id: parseInt(c.id),
          name: c.name,
          posRoles: rolesResponse ? rolesResponse.posRoles : [],
          revenueCenters: centersResponse ? centersResponse.revenueCenters : []
        });
      }
    },

    getCompanyRoles(companyId) {
      let company = this.getCompany(companyId);
      return company ? (company.posRoles || []) : [];
    },

    getCompanyRevenueCenters(companyId) {
      let company = this.getCompany(companyId);
      return company ? (company.revenueCenters || []) : [];
    },

    getCompany(companyId) {
      return this.companies.find(c => parseInt(c.id) === parseInt(companyId));
    },

    removeCompanyAssociation(index) {
      this.value.companies.splice(index, 1);
    },

    addCompanyAssociation() {
      this.value.companies.push({
        id: null,
        pin: '',
        posRole: { id: null },
        revenueCenter: { id: null }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .info-title {
    .title {
      font-weight: bold;
    }
    .subtitle {
      font-size: 14px;
      color: #808080;
      margin-bottom: 30px;
      font-weight: normal;
    }
  }
  .role-row {
    display: flex;
    justify-content: space-between;
    > div {
      width: 100%;
    }
    :not(:last-child) {
      margin-right: 10px;
      margin-bottom: 10px;
    }
  }
  .preferences {
    margin-top: 20px;
  }
  .radio_group {
    display: flex;
    padding-top: 10px;

    .radio {
      &:not(:last-child) {
        margin-right: 32px;
      }
    }
  }

  .separator {
    height: 1px;
    border-top: 1px dashed #d9d9d9;
    width: 100%;
    margin: 30px 0;
  }

  .pin-input {
    font-family: Monospace,serif;
  }

  .company-role__center {
    flex-grow: 1;
    display: flex;
  }

  .group__name-tooltip {
    display: flex;
    justify-content: space-between;
  }

  .company-role {
    display: flex;
    align-items: center;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    margin-bottom: 16px;

    .company-role__icon {
      fill: white;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      height: 32px;
      width: 32px;
      flex-shrink: 0;
      background: linear-gradient(135deg, #ED2937 0%, #FC6650 100%);
      margin-right: 12px;
    }

    .company-role__section {
      display: flex;
      align-items: center;
      flex-grow: 1;

      select {
        flex-grow: 1;
      }

      label {
        margin-right: 24px;
      }
    }

    .company-role__action-icon {
      fill: #fc6650;
      padding: 10px 10px 10px 20px;
      cursor: pointer;
    }

    .line {
      height: 32px;
      background-color: #e6e6e6;
      width: 1px;
      margin: 0 16px;
    }
  }

  .company-role__new {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: #fc6650;
    fill: #fc6650;
    border: 1px dashed #E6E6E6;
    cursor: pointer;

    .new__inner {
      display: flex;
      align-items: center;
      font-size: 14px;

      .svgicon {
        margin-right: 8px;
      }
    }
  }

  @media screen and (max-width: 800px) {
    .company-role__center {
      flex-direction: column;

      .company-role__section {
        margin-bottom: 10px;
        justify-content: space-between;
        padding-left: 15px;

        select {
          flex-grow: initial;
          width: 70%;
        }
      }

      .line  {
        display: none;
      }
    }
  }
</style>